/**
 * Utility functions for downloading chat conversations as HTML
 */

/**
 * Converts chat history to HTML format with preserved styling
 * @param {Array} chatHistory - Array of chat messages
 * @param {string} theme - Current theme ('light' or 'dark')
 * @returns {string} - HTML string
 */
export const generateChatHTML = (chatHistory, theme = 'dark') => {
  const currentDate = new Date().toLocaleString();
  
  const html = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>TLF Assistant Chat Export - ${currentDate}</title>
    <style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
            line-height: 1.6;
            color: ${theme === 'dark' ? '#e2e8f0' : '#333'};
            background: ${theme === 'dark' ? '#1a202c' : '#ffffff'};
            padding: 20px;
        }
        
        .chat-container {
            max-width: 1200px;
            margin: 0 auto;
            background: ${theme === 'dark' ? '#2d3748' : '#ffffff'};
            border-radius: 12px;
            box-shadow: 0 4px 20px rgba(0, 0, 0, ${theme === 'dark' ? '0.3' : '0.1'});
            overflow: hidden;
        }
        
        .chat-header {
            background: ${theme === 'dark' ? '#4a5568' : '#f8f9fa'};
            padding: 20px;
            border-bottom: 1px solid ${theme === 'dark' ? '#718096' : '#e9ecef'};
            text-align: center;
        }
        
        .chat-header h1 {
            color: ${theme === 'dark' ? '#e2e8f0' : '#333'};
            margin-bottom: 8px;
        }
        
        .chat-header p {
            color: ${theme === 'dark' ? '#a0aec0' : '#6c757d'};
            font-size: 14px;
        }
        
        .chat-messages {
            padding: 20px;
            max-height: 80vh;
            overflow-y: auto;
        }
        
        .message {
            margin-bottom: 20px;
            display: flex;
            align-items: flex-start;
            gap: 12px;
        }
        
        .message.user {
            flex-direction: row-reverse;
        }
        
        .message-avatar {
            width: 40px;
            height: 40px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
            font-size: 16px;
            flex-shrink: 0;
        }
        
        .message.user .message-avatar {
            background: #007bff;
            color: white;
        }
        
        .message.bot .message-avatar {
            background: ${theme === 'dark' ? '#4a90e2' : '#e3f2fd'};
            color: ${theme === 'dark' ? '#ffffff' : '#1976d2'};
        }
        
        .message-content {
            flex: 1;
            max-width: 70%;
        }
        
        .message.user .message-content {
            text-align: right;
        }
        
        .message-bubble {
            background: ${theme === 'dark' ? '#4a5568' : '#f8f9fa'};
            border: 1px solid ${theme === 'dark' ? '#718096' : '#e9ecef'};
            border-radius: 18px;
            padding: 12px 16px;
            word-wrap: break-word;
        }
        
        .message.user .message-bubble {
            background: #007bff;
            color: white;
            border-color: #007bff;
        }
        
        .message-time {
            font-size: 12px;
            color: ${theme === 'dark' ? '#a0aec0' : '#6c757d'};
            margin-top: 4px;
        }
        
        .message.user .message-time {
            text-align: right;
        }
        
        /* Code blocks */
        pre {
            background: ${theme === 'dark' ? '#1a202c' : '#f8f9fa'};
            border: 1px solid ${theme === 'dark' ? '#4a5568' : '#e9ecef'};
            border-radius: 8px;
            padding: 16px;
            overflow-x: auto;
            margin: 8px 0;
        }
        
        code {
            font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
            font-size: 14px;
            color: ${theme === 'dark' ? '#e2e8f0' : '#333'};
        }
        
        /* Tables */
        table {
            width: 100%;
            border-collapse: collapse;
            margin: 12px 0;
            background: ${theme === 'dark' ? '#2d3748' : '#ffffff'};
            border-radius: 8px;
            overflow: hidden;
            box-shadow: 0 2px 8px rgba(0, 0, 0, ${theme === 'dark' ? '0.2' : '0.1'});
        }
        
        th, td {
            padding: 12px;
            text-align: left;
            border-bottom: 1px solid ${theme === 'dark' ? '#4a5568' : '#e9ecef'};
        }
        
        th {
            background: ${theme === 'dark' ? '#4a5568' : '#f8f9fa'};
            font-weight: 600;
            color: ${theme === 'dark' ? '#e2e8f0' : '#333'};
        }
        
        tr:hover {
            background: ${theme === 'dark' ? '#4a5568' : '#f8f9fa'};
        }
        
        /* Lists */
        ul, ol {
            margin: 8px 0;
            padding-left: 20px;
        }
        
        li {
            margin: 4px 0;
        }
        
        /* Links */
        a {
            color: #007bff;
            text-decoration: none;
        }
        
        a:hover {
            text-decoration: underline;
        }
        
        /* Images */
        img {
            max-width: 100%;
            height: auto;
            border-radius: 8px;
            margin: 8px 0;
        }
        
        /* File downloads */
        .file-download {
            display: inline-block;
            background: #28a745;
            color: white;
            padding: 8px 16px;
            border-radius: 6px;
            text-decoration: none;
            margin: 4px 0;
        }
        
        .file-download:hover {
            background: #218838;
            text-decoration: none;
        }
        
        /* Print styles */
        @media print {
            body {
                background: white;
                color: black;
            }
            
            .chat-container {
                box-shadow: none;
                border: 1px solid #ccc;
            }
            
            .message-bubble {
                break-inside: avoid;
            }
        }
    </style>
</head>
<body>
    <div class="chat-container">
        <div class="chat-header">
            <h1>🚀 TLF Assistant Chat Export</h1>
            <p>Exported on ${currentDate}</p>
        </div>
        <div class="chat-messages">
            ${chatHistory.map((message, index) => generateMessageHTML(message, index)).join('')}
        </div>
    </div>
</body>
</html>`;

  return html;
};

/**
 * Generates HTML for a single message
 * @param {Object} message - Message object
 * @param {number} index - Message index
 * @returns {string} - HTML string for the message
 */
const generateMessageHTML = (message, index) => {
  const timestamp = new Date().toLocaleTimeString();
  const isUser = message.type === 'user';
  
  let content = '';
  
  if (message.type === 'thinking') {
    content = `
      <div class="thinking-indicator">
        <span class="dot">•</span>
        <span class="dot">•</span>
        <span class="dot">•</span>
        <span>Thinking...</span>
      </div>
    `;
  } else if (message.file) {
    content = generateFileHTML(message.file);
  } else if (message.graph) {
    content = `<div class="graph-placeholder">📊 Graph visualization (not exportable)</div>`;
  } else {
    content = formatMessageContent(message.message);
  }
  
  return `
    <div class="message ${isUser ? 'user' : 'bot'}">
      <div class="message-avatar">
        ${isUser ? 'U' : '🤖'}
      </div>
      <div class="message-content">
        <div class="message-bubble">
          ${content}
        </div>
        <div class="message-time">
          ${timestamp}
        </div>
      </div>
    </div>
  `;
};

/**
 * Generates HTML for file messages
 * @param {Object} file - File object
 * @returns {string} - HTML string for the file
 */
const generateFileHTML = (file) => {
  if (file.type === 'image') {
    return `
      <div class="file-message">
        <img src="data:image/png;base64,${file.data || 'placeholder'}" alt="${file.name}" />
        <p>📊 ${file.name}</p>
      </div>
    `;
  } else {
    return `
      <div class="file-message">
        <a href="#" class="file-download">📥 Download: ${file.name}</a>
      </div>
    `;
  }
};

/**
 * Formats message content with basic markdown-like formatting
 * @param {string} content - Message content
 * @returns {string} - Formatted HTML content
 */
const formatMessageContent = (content) => {
  if (!content) return '';
  
  // Convert basic markdown to HTML
  let formatted = content
    // Bold text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    // Italic text
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    // Code blocks
    .replace(/```([\\s\\S]*?)```/g, '<pre><code>$1</code></pre>')
    // Inline code
    .replace(/`(.*?)`/g, '<code>$1</code>')
    // Line breaks
    .replace(/\\n/g, '<br>');
  
  return formatted;
};

/**
 * Downloads the chat as an HTML file
 * @param {Array} chatHistory - Array of chat messages
 * @param {string} theme - Current theme
 */
export const downloadChatAsHTML = (chatHistory, theme = 'dark') => {
  const html = generateChatHTML(chatHistory, theme);
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = `tlf-assistant-chat-${new Date().toISOString().split('T')[0]}.html`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
